import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";

interface MisconceptionAdvancedData {
  detectionMethods?: string[];
  reEvaluationMethods?: string[];
  customContext?: string;
  [key: string]: any;
}

export const ReEvaluationMethodsStep: React.FC<
  StepComponentProps<MisconceptionAdvancedData>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomMethod, setShowCustomMethod] = useState(false);
  const [customMethodText, setCustomMethodText] = useState("");

  const predefinedMethods = [
    {
      id: "Post-lesson concept mapping",
      emoji: "🗺️",
      label: t("misconceptions.reevaluation.postConceptMapping"),
      desc: t("misconceptions.reevaluation.postConceptMappingDesc"),
    },
    {
      id: "Peer teaching",
      emoji: "👥",
      label: t("misconceptions.reevaluation.peerTeaching"),
      desc: t("misconceptions.reevaluation.peerTeachingDesc"),
    },
    {
      id: "Real-world application tasks",
      emoji: "🌍",
      label: t("misconceptions.reevaluation.realWorldTasks"),
      desc: t("misconceptions.reevaluation.realWorldTasksDesc"),
    },
    {
      id: "Reflection journals",
      emoji: "📔",
      label: t("misconceptions.reevaluation.reflectionJournals"),
      desc: t("misconceptions.reevaluation.reflectionJournalsDesc"),
    },
    {
      id: "Progressive problem solving",
      emoji: "🧩",
      label: t("misconceptions.reevaluation.progressiveProblemSolving"),
      desc: t("misconceptions.reevaluation.progressiveProblemSolvingDesc"),
    },
    {
      id: "Before-after comparisons",
      emoji: "⚖️",
      label: t("misconceptions.reevaluation.beforeAfterComparisons"),
      desc: t("misconceptions.reevaluation.beforeAfterComparisonsDesc"),
    },
    {
      id: "Teach-back demonstrations",
      emoji: "🎭",
      label: t("misconceptions.reevaluation.teachBackDemonstrations"),
      desc: t("misconceptions.reevaluation.teachBackDemonstrationsDesc"),
    },
    {
      id: "Misconception identification exercises",
      emoji: "🎯",
      label: t("misconceptions.reevaluation.misconceptionIdentification"),
      desc: t("misconceptions.reevaluation.misconceptionIdentificationDesc"),
    },
  ];

  const selectedMethods = data.reEvaluationMethods || [];

  const handleMethodToggle = (methodId: string) => {
    const method = predefinedMethods.find((m) => m.id === methodId);
    const methodLabel = method ? method.label : methodId;

    const updatedMethods = selectedMethods.includes(methodLabel)
      ? selectedMethods.filter((method) => method !== methodLabel)
      : [...selectedMethods, methodLabel];

    onDataChange({ reEvaluationMethods: updatedMethods });
  };

  const handleCustomMethodAdd = () => {
    if (customMethodText.trim()) {
      const updatedMethods = [...selectedMethods, customMethodText.trim()];
      onDataChange({ reEvaluationMethods: updatedMethods });
      setCustomMethodText("");
      setShowCustomMethod(false);
    }
  };

  const isMethodSelected = (methodId: string) => {
    const method = predefinedMethods.find((m) => m.id === methodId);
    const methodLabel = method ? method.label : methodId;
    return selectedMethods.includes(methodLabel);
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("misconceptions.steps.reevaluation.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("misconceptions.steps.reevaluation.description")}
        </p>
      </div>

      {/* Method Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("misconceptions.form.reevaluationMethods")}
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t("misconceptions.form.selectMultiple")}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {predefinedMethods.map((method) => (
            <div
              key={method.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isMethodSelected(method.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleMethodToggle(method.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isMethodSelected(method.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isMethodSelected(method.id) && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{method.emoji}</span>
                    <h3 className="font-medium text-base">{method.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {method.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Custom Method */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("misconceptions.form.customReEvaluationMethod")}
          </label>
          {!showCustomMethod && (
            <button
              type="button"
              onClick={() => setShowCustomMethod(true)}
              className={`text-sm text-${themeColor}-600 dark:text-${themeColor}-400 hover:text-${themeColor}-700 dark:hover:text-${themeColor}-300 font-medium`}
            >
              {t("misconceptions.form.addCustom")}
            </button>
          )}
        </div>

        {showCustomMethod && (
          <div className="flex gap-3">
            <input
              type="text"
              value={customMethodText}
              onChange={(e) => setCustomMethodText(e.target.value)}
              placeholder={t(
                "misconceptions.form.customReEvaluationPlaceholder",
              )}
              className={`flex-1 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-3 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
            />
            <button
              type="button"
              onClick={handleCustomMethodAdd}
              disabled={!customMethodText.trim()}
              className={`px-4 py-3 rounded-xl bg-${themeColor}-600 text-white font-medium hover:bg-${themeColor}-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200`}
            >
              {t("common.add")}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCustomMethod(false);
                setCustomMethodText("");
              }}
              className="px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 font-medium hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
      </div>

      {/* Selected Methods Summary */}
      {selectedMethods.length > 0 && (
        <div className="space-y-3">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("misconceptions.form.selectedMethods")} ({selectedMethods.length}
            )
          </label>
          <div className="flex flex-wrap gap-2">
            {selectedMethods.map((method, index) => (
              <div
                key={index}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-${themeColor}-100 dark:bg-${themeColor}-900/30 text-${themeColor}-700 dark:text-${themeColor}-300 text-sm`}
              >
                <span>{method}</span>
                <button
                  type="button"
                  onClick={() => {
                    const methodObj = predefinedMethods.find(
                      (m) => m.label === method,
                    );
                    if (methodObj) {
                      handleMethodToggle(methodObj.id);
                    } else {
                      // Handle custom method removal
                      const updatedMethods = selectedMethods.filter(
                        (m) => m !== method,
                      );
                      onDataChange({ reEvaluationMethods: updatedMethods });
                    }
                  }}
                  className={`text-${themeColor}-500 hover:text-${themeColor}-700 dark:hover:text-${themeColor}-200`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connection to Detection Methods */}
      {data.detectionMethods && data.detectionMethods.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-400">
          <div className="flex items-start gap-3">
            <div className="text-blue-500 mt-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-1">
                {t("misconceptions.form.detectionConnection")}
              </h4>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                {t("misconceptions.form.detectionConnectionDesc")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
